import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import type * as React from 'react';
import interviewsFetched, {
	type InterviewPayload,
	type InterviewsFetchedAction,
} from '@Application/Beneficiaries/Interviews/Actions/interviewsFetched';
import interviewsFetchFailed, {
	type InterviewsFetchFailedAction,
} from '@Application/Beneficiaries/Interviews/Actions/interviewsFetchFailed';
import type FetchInterviews from '@Application/Beneficiaries/Interviews/Queries/FetchInterviews';
import { getPaginated } from '@Domain/Api';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';

type Actions = InterviewsFetchedAction | InterviewsFetchFailedAction;

// `getPaginated` requires a mutable `results` array (it pushes subsequent pages into it); the
// dispatched action still carries the `readonly` form declared by `InterviewsFetchedPayload`.
type PaginatedInterviews = {
	totalRecords: number;
	results: InterviewPayload[];
};

export default (query: FetchInterviews) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Page indexing starts at 0 server-side, and `getPaginated` only walks every page when called
			// with `page: 0` — its internal guard only catches `page > 1`. `rowsPerPage: 200` is the
			// server's maximum accepted `rows` value.
			const payload = await getPaginated<PaginatedInterviews>(
				`${buildSubjectApiPath(query.facilityId, query.subject)}/interviews?${query.toQueryString()}`,
				{ page: 0, rowsPerPage: 200 },
			);

			const action = interviewsFetched(
				payload,
				query.facilityId,
				query.subject.subjectId,
				query.subject.typeKey,
			);
			dispatch(action);
			return action;
		} catch (error) {
			const action = interviewsFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
